import { Env } from '@mhe/reader/types';

export const CAT_SCRIPT_PATH = '/artifacts/lsa-artifact-wc.min.js';
export const LOCALSTORAGE_KEY = 'CAT_WEBCOMPONENT_URL';

const CAT_INTERNAL_BASE_URLS: Partial<Record<Env, string>> = {
  local: 'https://dev.cat.afl.mh-int.dev',
  dev: 'https://dev.cat.afl.mh-int.dev',
  qastg: 'https://qastg.cat.afl.mh-int.dev',
  qalv: 'https://qalv.cat.afl.mh-int.dev',
  // prod intentionally omitted — prod uses Scout external only
};

export function isValidUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
}

export interface CatUrlResolver {
  resolve: () => string | null
}

/**
 * Resolver 1 (non-prod only): reads a developer-supplied URL from localStorage.
 * Returns the trimmed value or null if absent, empty, whitespace, or inaccessible.
 */
export class LocalStorageResolver implements CatUrlResolver {
  constructor(private readonly storage: Storage) {}

  resolve(): string | null {
    try {
      const value = this.storage.getItem(LOCALSTORAGE_KEY)?.trim() ?? null;
      return value || null;
    } catch {
      console.warn('[CatWebcomponentLoader] localStorage is unavailable, skipping LocalStorageResolver');
      return null;
    }
  }
}

/**
 * Resolver 2 (non-prod only): returns the hardcoded internal base URL for the
 * current SDLC environment plus the well-known script path.
 * Returns null when no internal URL exists for the given env (e.g. prod).
 */
export class HardcodedInternalResolver implements CatUrlResolver {
  constructor(private readonly env: Env) {}

  resolve(): string | null {
    const base = CAT_INTERNAL_BASE_URLS[this.env];
    return base ? base + CAT_SCRIPT_PATH : null;
  }
}

/**
 * Resolver 3 (all envs): returns the Scout-registered external base URL plus
 * the well-known script path. Returns null when the Scout base URL is empty
 * (e.g. local environment where Scout is not queried).
 */
export class ScoutExternalResolver implements CatUrlResolver {
  constructor(private readonly scoutBaseUrl: string) {}

  resolve(): string | null {
    return this.scoutBaseUrl ? this.scoutBaseUrl + CAT_SCRIPT_PATH : null;
  }
}

/**
 * Runs each resolver in order and returns the first result that is a valid
 * http/https URL. Returns null if no resolver produces a valid URL.
 */
export function resolveFirstValidUrl(resolvers: CatUrlResolver[]): string | null {
  for (const resolver of resolvers) {
    const url = resolver.resolve();
    if (url && isValidUrl(url)) {
      return url;
    }
  }
  return null;
}
